import React, { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud, Play, Pause, Download, Loader2, Lock, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { mastering, MASTER_PRESETS, DEFAULT_CONTROLS } from "@/audio/mastering";
import { downloadBlob } from "@/audio/wav";
import { useSubscription } from "@/subscription/SubscriptionContext";
import InfoDot from "@/studio/InfoDot";

const fmtTime = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const SLIDERS = [
  { key: "bass", label: "Bass", min: -10, max: 10, step: 0.5, unit: "dB", hint: "Low-end weight (808s / kick)" },
  { key: "mid", label: "Mid", min: -10, max: 10, step: 0.5, unit: "dB", hint: "Body & vocals (1.2 kHz)" },
  { key: "air", label: "Air", min: -10, max: 10, step: 0.5, unit: "dB", hint: "Brightness & sparkle (highs)" },
  { key: "warmth", label: "Warmth", min: 0, max: 100, step: 1, unit: "%", hint: "Analog-style saturation & fullness" },
  { key: "punch", label: "Punch / Glue", min: 0, max: 100, step: 1, unit: "%", hint: "Compression that ties it together" },
  { key: "loudness", label: "Loudness", min: 0, max: 100, step: 1, unit: "%", hint: "Overall volume into the limiter" },
];

export default function Mastering() {
  const { isPro, openUpgrade } = useSubscription();
  const fileRef = useRef(null);
  const rafRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [bypass, setBypass] = useState(false); // true = A (original)
  const [preset, setPreset] = useState("balanced");
  const [controls, setControls] = useState({ ...DEFAULT_CONTROLS });
  const [lufs, setLufs] = useState(-70);
  const [rendering, setRendering] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // live meter + playhead loop
  useEffect(() => {
    const tick = () => {
      if (mastering.isPlaying) {
        setPosition(mastering.position);
        setPlaying(true);
      }
      setLufs(mastering.loudness());
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    window.__mastering = mastering;
    mastering.onended = () => {
      setPlaying(false);
      setPosition(0);
    };
    return () => {
      mastering.stop();
      mastering.onended = null;
    };
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.(wav|mp3|m4a|aac|ogg|flac)$/i.test(file.name)) {
      toast.error("Please upload an audio file (WAV, MP3, M4A…)");
      return;
    }
    try {
      const info = await mastering.loadFile(file);
      setName(info.name);
      setDuration(info.duration);
      setPosition(0);
      setPlaying(false);
      setLoaded(true);
      toast.success("Track loaded — hit play and shape the sound");
    } catch (e) {
      toast.error("Couldn't read that file. Try a WAV or MP3.");
    }
  }, []);

  const togglePlay = () => {
    if (!loaded) return;
    if (mastering.isPlaying) {
      mastering.pause();
      setPlaying(false);
    } else {
      mastering.play();
      setPlaying(true);
    }
  };

  const onSeek = (e) => {
    const v = parseFloat(e.target.value);
    mastering.seek(v);
    setPosition(v);
  };

  const setControl = (key, value) => {
    const next = { ...controls, [key]: value };
    setControls(next);
    mastering.setControls({ [key]: value });
    setPreset("custom");
  };

  const applyPreset = (key) => {
    const vals = mastering.applyPreset(key);
    if (vals) {
      setControls((c) => ({ ...c, ...vals }));
      setPreset(key);
    }
  };

  const toggleAB = () => {
    const next = !bypass;
    mastering.setBypass(next);
    setBypass(next);
  };

  const doDownload = async () => {
    if (!loaded) return;
    if (!isPro) {
      openUpgrade("wav-master");
      return;
    }
    setRendering(true);
    try {
      const blob = await mastering.renderWav();
      if (blob) {
        downloadBlob(blob, `${name || "master"}-mastered.wav`);
        toast.success("Hi-def WAV downloaded 🎚️");
      }
    } catch (e) {
      toast.error("Render failed — try again.");
    } finally {
      setRendering(false);
    }
  };

  // Loudness meter fill (map -30..0 LUFS to 0..100%)
  const meterPct = Math.max(0, Math.min(100, ((lufs + 30) / 30) * 100));
  const meterColor = lufs > -9 ? "#ff007f" : lufs > -16 ? "#ffb300" : "#00e5ff";

  return (
    <div className="h-full overflow-y-auto p-6 flex flex-col gap-6 max-w-4xl mx-auto w-full" data-testid="mastering-view">
      {/* header */}
      <div className="flex items-center justify-center gap-3">
        <div className="h-px flex-1 bg-[#262626]" />
        <span className="hw-label flex items-center gap-1.5">
          Mastering — upload, shape the sound, download hi-def
          <InfoDot section="mastering" label="How mastering works" />
        </span>
        <div className="h-px flex-1 bg-[#262626]" />
      </div>

      {/* upload zone */}
      {!loaded ? (
        <div
          data-testid="master-dropzone"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed cursor-pointer py-20 transition-colors ${
            dragOver ? "border-[#00e5ff] bg-[#00e5ff]/5" : "border-[#333] hover:border-[#555] bg-[#0a0a0a]"
          }`}
        >
          <UploadCloud size={44} className="text-[#00e5ff]" />
          <div className="text-center">
            <p className="font-display text-lg font-bold text-neutral-100">Drop your finished song here</p>
            <p className="text-sm text-neutral-500 mt-1">WAV, MP3, M4A — we'll master it in your browser</p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.wav,.mp3,.m4a,.aac,.ogg,.flac"
            className="hidden"
            data-testid="master-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      ) : (
        <>
          {/* transport + track name */}
          <div className="rounded-lg border border-[#262626] bg-[#0a0a0a] p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <button
                data-testid="master-play-btn"
                onClick={togglePlay}
                className="w-12 h-12 rounded-full bg-[#00e5ff] text-black flex items-center justify-center hover:brightness-110 transition-all shrink-0"
              >
                {playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm font-bold text-neutral-100 truncate" data-testid="master-track-name">
                  {name}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono-data text-[10px] text-neutral-500 tabular-nums">{fmtTime(position)}</span>
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.01}
                    value={position}
                    onChange={onSeek}
                    data-testid="master-seek"
                    className="flex-1 accent-[#00e5ff] h-1"
                  />
                  <span className="font-mono-data text-[10px] text-neutral-500 tabular-nums">{fmtTime(duration)}</span>
                </div>
              </div>
              <button
                data-testid="master-ab-btn"
                onClick={toggleAB}
                className={`px-4 py-2 rounded-sm border text-xs font-bold uppercase tracking-widest transition-colors shrink-0 ${
                  bypass
                    ? "border-[#666] text-neutral-300 bg-[#1a1a1a]"
                    : "border-[#00e5ff] text-[#00e5ff] bg-[#00e5ff]/10"
                }`}
                title="Flip between your Original (A) and the Mastered version (B)"
              >
                {bypass ? "A · Original" : "B · Mastered"}
              </button>
            </div>

            {/* loudness meter */}
            <div className="flex items-center gap-3">
              <span className="hw-label text-[10px] shrink-0">Loudness</span>
              <div className="flex-1 h-3 rounded-full bg-[#141414] overflow-hidden border border-[#262626]">
                <div
                  data-testid="master-lufs-bar"
                  className="h-full rounded-full transition-all duration-100"
                  style={{ width: `${meterPct}%`, backgroundColor: meterColor }}
                />
              </div>
              <span className="font-mono-data text-[11px] tabular-nums w-24 text-right" style={{ color: meterColor }} data-testid="master-lufs">
                {isFinite(lufs) ? lufs.toFixed(1) : "-∞"} LUFS
              </span>
            </div>
            <p className="text-[10px] text-neutral-600 -mt-1">
              Streaming target ≈ −14 LUFS (cyan). Amber/pink = getting loud & squeezed.
            </p>
          </div>

          {/* presets */}
          <div>
            <span className="hw-label text-[10px]">Presets</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(MASTER_PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  data-testid={`master-preset-${key}`}
                  onClick={() => applyPreset(key)}
                  className={`px-4 py-2 rounded-sm border text-xs font-bold transition-colors ${
                    preset === key
                      ? "border-[#ff007f] text-[#ff007f] bg-[#ff007f]/10"
                      : "border-[#333] text-neutral-300 hover:border-[#555]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {preset === "custom" && (
                <span className="px-3 py-2 text-[10px] text-neutral-500 uppercase tracking-widest self-center">
                  Custom
                </span>
              )}
            </div>
          </div>

          {/* manual controls */}
          <div className="grid gap-4">
            {SLIDERS.map((s) => (
              <div key={s.key} className="flex items-center gap-4">
                <div className="w-28 shrink-0">
                  <span className="font-display text-xs font-bold text-neutral-200">{s.label}</span>
                  <p className="text-[9px] text-neutral-600 leading-tight">{s.hint}</p>
                </div>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={controls[s.key]}
                  onChange={(e) => setControl(s.key, parseFloat(e.target.value))}
                  data-testid={`master-ctrl-${s.key}`}
                  className="flex-1 accent-[#00e5ff]"
                />
                <span className="font-mono-data text-[11px] tabular-nums w-16 text-right text-neutral-400">
                  {controls[s.key] > 0 && s.unit === "dB" ? "+" : ""}
                  {controls[s.key]}
                  {s.unit}
                </span>
              </div>
            ))}
          </div>

          {/* actions */}
          <div className="flex items-center gap-3 pt-2 border-t border-[#262626]">
            <button
              data-testid="master-change-track-btn"
              onClick={() => {
                mastering.stop();
                setLoaded(false);
                setPlaying(false);
              }}
              className="flex items-center gap-2 px-4 py-3 rounded-sm border border-[#333] text-neutral-300 hover:border-[#555] text-xs font-bold"
            >
              <RefreshCw size={14} /> New Track
            </button>
            <button
              data-testid="master-download-btn"
              onClick={doDownload}
              disabled={rendering}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#00e5ff] text-black font-bold hover:brightness-110 transition-all disabled:opacity-60 text-sm"
            >
              {rendering ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Rendering hi-def WAV…
                </>
              ) : isPro ? (
                <>
                  <Download size={16} /> Download Hi-Def WAV
                </>
              ) : (
                <>
                  <Lock size={16} /> Download Hi-Def WAV — Go Pro
                </>
              )}
            </button>
          </div>
          {!isPro && (
            <p className="text-[11px] text-neutral-500 -mt-3 flex items-center gap-1.5" data-testid="master-pro-note">
              <Sparkles size={12} className="text-[#ff007f]" />
              Preview and tweak free — Pro unlocks the hi-def WAV download.
            </p>
          )}
        </>
      )}
    </div>
  );
}
